import * as React from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { debounce } from 'underscore';

import { usePutWorkspaceQuizLock } from '~teacher/hook/workspaces/usePutWorkspaceQuizLock';
import { refetchWorkspaceQuizLock } from '~teacher/hook/workspaces/useWorkspaceQuizLock';

const WORKSPACE_LOCK_REFETCH_INTERVAL = 60 * 1000; // 60 * 1000 = 1 minute

interface Props {
  workspaceId: string;
  workspaceQuizId: string;
  quizId: number;
}

export function RefreshWorkspaceLock({
  workspaceQuizId,
  workspaceId,
  quizId,
}: Props) {
  const queryClient = useQueryClient();
  const putWorkspaceQuizLock = usePutWorkspaceQuizLock();
  const refetchLock = React.useRef<(this: Document, ev: Event) => any>(
    debounce((event) => {
      if (__DEV__) {
        console.debug(
          `Re-fetched workspace lock at ${new Date()} because of event ${
            event.type
          }`
        );
      }
      return refetchWorkspaceQuizLock({
        queryClient,
        workspaceId,
        workspaceQuizId,
      });
    }, WORKSPACE_LOCK_REFETCH_INTERVAL)
  );

  const relinquishLock = React.useRef(() => {
    putWorkspaceQuizLock.mutate({ workspaceId, workspaceQuizId, quizId });
  });

  React.useEffect(() => {
    // When this component unmounts, we want to relinquish the lock on the quiz
    return relinquishLock.current;
  }, []);

  React.useEffect(() => {
    const refetch = refetchLock.current;
    document.addEventListener('mousemove', refetch);
    document.addEventListener('keypress', refetch);
    document.addEventListener('scroll', refetch);
    return () => {
      document.removeEventListener('mousemove', refetch);
      document.removeEventListener('keypress', refetch);
      document.removeEventListener('scroll', refetch);
    };
  }, []);

  return null;
}
